/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from "react";
// UNCOMMENT THE LINES BELOW IN YOUR LOCAL PROJECT AFTER INSTALLING PACKAGES
import {
  GoogleMap,
  useJsApiLoader,
  Polyline,
  Marker,
} from "@react-google-maps/api"; // Added Marker
import { Card, Button, Space, Tag, Spin, Alert, Switch } from "antd"; // Using Ant Design components
import { CloseOutlined, Loading3QuartersOutlined } from "@ant-design/icons";

const antIcon = <Loading3QuartersOutlined spin />;

// --- Constants ---
const GOOGLE_MAPS_API_KEY = "AIzaSyDcQQw5LJ5YLntPe4kzR_8w-TKGumSFmfI"; // REPLACE WITH YOUR ACTUAL API KEY
const MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "100%",
  borderRadius: "0",
  overflow: "hidden",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
};

const LIBRARIES: ("drawing" | "geometry" | "places" | "visualization")[] = [
  "geometry",
];

// --- Custom Map Styles (inspired by your image) ---
// const CUSTOM_MAP_STYLES = [
//   {
//     elementType: "geometry",
//     stylers: [
//       {
//         color: "#ebe3cd",
//       },
//     ],
//   },
//   {
//     elementType: "labels.text.fill",
//     stylers: [
//       {
//         color: "#523735",
//       },
//     ],
//   },
//   {
//     elementType: "labels.text.stroke",
//     stylers: [
//       {
//         color: "#f5f1e6",
//       },
//     ],
//   },
//   {
//     featureType: "administrative",
//     elementType: "geometry.stroke",
//     stylers: [
//       {
//         color: "#c9b2a6",
//       },
//     ],
//   },
//   {
//     featureType: "administrative.land_parcel",
//     elementType: "geometry.stroke",
//     stylers: [
//       {
//         color: "#dcd2be",
//       },
//     ],
//   },
//   {
//     featureType: "administrative.land_parcel",
//     elementType: "labels.text.fill",
//     stylers: [
//       {
//         color: "#ae9e90",
//       },
//     ],
//   },
//   {
//     featureType: "landscape.natural",
//     elementType: "geometry",
//     stylers: [
//       {
//         color: "#dfd2ae",
//       },
//     ],
//   },
//   {
//     featureType: "poi",
//     elementType: "geometry",
//     stylers: [
//       {
//         color: "#dfd2ae",
//       },
//     ],
//   },
//   {
//     featureType: "poi",
//     elementType: "labels.text.fill",
//     stylers: [
//       {
//         color: "#93817c",
//       },
//     ],
//   },
//   {
//     featureType: "poi.park",
//     elementType: "geometry.fill",
//     stylers: [
//       {
//         color: "#a5b076",
//       },
//     ],
//   },
//   {
//     featureType: "poi.park",
//     elementType: "labels.text.fill",
//     stylers: [
//       {
//         color: "#447530",
//       },
//     ],
//   },
//   {
//     featureType: "road",
//     elementType: "geometry",
//     stylers: [
//       {
//         color: "#f5f1e6",
//       },
//     ],
//   },
//   {
//     featureType: "road.arterial",
//     elementType: "geometry",
//     stylers: [
//       {
//         color: "#fdfcf8",
//       },
//     ],
//   },
//   {
//     featureType: "road.highway",
//     elementType: "geometry",
//     stylers: [
//       {
//         color: "#f8c967",
//       },
//     ],
//   },
//   {
//     featureType: "road.highway",
//     elementType: "geometry.stroke",
//     stylers: [
//       {
//         color: "#e9bc62",
//       },
//     ],
//   },
//   {
//     featureType: "road.highway.controlled_access",
//     elementType: "geometry",
//     stylers: [
//       {
//         color: "#e98d58",
//       },
//     ],
//   },
//   {
//     featureType: "road.highway.controlled_access",
//     elementType: "geometry.stroke",
//     stylers: [
//       {
//         color: "#db8555",
//       },
//     ],
//   },
//   {
//     featureType: "road.local",
//     elementType: "labels.text.fill",
//     stylers: [
//       {
//         color: "#806b63",
//       },
//     ],
//   },
//   {
//     featureType: "transit.line",
//     elementType: "geometry",
//     stylers: [
//       {
//         color: "#dfd2ae",
//       },
//     ],
//   },
//   {
//     featureType: "transit.line",
//     elementType: "labels.text.fill",
//     stylers: [
//       {
//         color: "#8f7d77",
//       },
//     ],
//   },
//   {
//     featureType: "transit.line",
//     elementType: "labels.text.stroke",
//     stylers: [
//       {
//         color: "#ebe3cd",
//       },
//     ],
//   },
//   {
//     featureType: "transit.station",
//     elementType: "geometry",
//     stylers: [
//       {
//         color: "#dfd2ae",
//       },
//     ],
//   },
//   {
//     featureType: "water",
//     elementType: "geometry.fill",
//     stylers: [
//       {
//         color: "#b9d3c2",
//       },
//     ],
//   },
//   {
//     featureType: "water",
//     elementType: "labels.text.fill",
//     stylers: [
//       {
//         color: "#92998d",
//       },
//     ],
//   },
// ];

// --- New Data Interfaces based on provided structure ---
interface Visit {
  id: string;
  title: string;
  status: string;
  startTime: string;
  endTime: string;
  address: string;
  contract: string;
  property: {
    latitude: string;
    longitude: string;
  };
}

interface CrewRouteData {
  id: string;
  crew_name: string;
  crew_leader: string;
  hours: string;
  visits: Visit[];
  starting_crew_yard_longitude: string;
  starting_crew_yard_latitude: string;
  finish_crew_yard_longitude: string;
  finish_crew_yard_latitude: string;
}

// Interface for processed route data to be displayed on map
interface ProcessedRoute {
  id: string;
  name: string;
  color: string;
  polyline?: {
    path: Array<{ lat: number; lng: number }>;
    strokeColor: string;
    strokeOpacity: number;
    strokeWeight: number;
    zIndex: number;
  };
  visible: boolean;
  isActive: boolean;
  visits: Visit[]; // Store visits to render markers
  crew_name: string;
}

// --- Main Map Component ---
const RoutesMap = ({ routeData, setShowRoutes }: any) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  console.log("routeData in map:", routeData);

  const mapRef = useRef<any>(null);
  const directionsServiceRef = useRef<any>(null);

  const [routesData, setRoutesData] = useState<ProcessedRoute[]>([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // Helper to generate a consistent color for a given string (crew ID)
  const predefinedColors = [
    "#6454F4", // 1st
    "#943AF5", // 2nd
    "#D33D3D", // 3rd
    "#43A100", // 4th
  ];

  const colorMap = new Map<string, string>();

  const stringToColor = (str: string) => {
    // Assign fixed colors to first unique strings
    if (!colorMap.has(str)) {
      if (colorMap.size < predefinedColors.length) {
        colorMap.set(str, predefinedColors[colorMap.size]);
      } else {
        // Fallback to generated color
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
          hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        let color = "#";
        for (let i = 0; i < 3; i++) {
          const value = (hash >> (i * 8)) & 0xff;
          color += ("00" + value.toString(16)).substr(-2);
        }
        colorMap.set(str, color);
      }
    }

    return colorMap.get(str)!;
  };

  // Function to calculate and fetch directions for all crew routes
  const fetchAllRoutes = useCallback(
    async (service: any, currentRouteData: CrewRouteData[]) => {
      setLoadingRoutes(true);
      setError(null);
      console.log("Fetching routes for crews:", currentRouteData);
      if (
        currentRouteData.length > 0 &&
        currentRouteData?.[0]?.starting_crew_yard_longitude
      ) {
        setMapCenter({
          lat: Number(currentRouteData?.[0]?.starting_crew_yard_latitude),
          lng: Number(currentRouteData?.[0]?.starting_crew_yard_longitude),
        });
      } else {
        setMapCenter({
          lat: Number(currentRouteData?.[0]?.visits[0]?.property.latitude),
          lng: Number(currentRouteData?.[0]?.visits[0]?.property.longitude),
        });
      }
      // Inject starting yard as first visit (if not already same coords)
      currentRouteData = currentRouteData.map((crew: any) => {
        // Inject finishing yard as last visit (if not already same coords)
        {
          // Slightly adjust finish yard coordinates if identical (or nearly) to starting yard
          if (
            crew.finish_crew_yard_latitude &&
            crew.finish_crew_yard_longitude &&
            crew.starting_crew_yard_latitude &&
            crew.starting_crew_yard_longitude
          ) {
            const startLatNum = Number(crew.starting_crew_yard_latitude);
            const startLngNum = Number(crew.starting_crew_yard_longitude);
            let finishLatNum = Number(crew.finish_crew_yard_latitude);
            let finishLngNum = Number(crew.finish_crew_yard_longitude);

            const nearSame =
              Math.abs(startLatNum - finishLatNum) < 1e-7 &&
              Math.abs(startLngNum - finishLngNum) < 1e-7;

            if (nearSame) {
              const OFFSET = 0.00012; // small offset to separate markers
              finishLatNum = startLatNum + OFFSET;
              finishLngNum = startLngNum + OFFSET;
              crew = {
                ...crew,
                finish_crew_yard_latitude: String(finishLatNum),
                finish_crew_yard_longitude: String(finishLngNum),
              };
            }
          }
          const finishLat = crew.finish_crew_yard_latitude;
          const finishLng = crew.finish_crew_yard_longitude;
          const finishName = crew.finish_crew_yard_name;
          if (finishLat && finishLng && finishName) {
            const finishVisit: Visit = {
              id: `${crew.id}-finish-yard`,
              title: finishName,
              status: "YARD_FINISH",
              startTime: crew.finishing_point_start_time || "",
              endTime: crew.finishing_point_end_time || "",
              address: crew.finish_crew_yard_address || "",
              contract: "",
              property: {
                latitude: String(finishLat),
                longitude: String(finishLng),
              },
            };
            const last = crew.visits?.[crew.visits.length - 1];
            const duplicate =
              last &&
              Number(last.property.latitude) ===
                Number(finishVisit.property.latitude) &&
              Number(last.property.longitude) ===
                Number(finishVisit.property.longitude);
            if (!duplicate) {
              crew = { ...crew, visits: [...(crew.visits || []), finishVisit] };
            }
          }
        }
        const yardLat = crew.starting_crew_yard_latitude;
        const yardLng = crew.starting_crew_yard_longitude;
        const yardName = crew.starting_crew_yard_name;
        if (yardLat && yardLng && yardName) {
          const yardVisit: Visit = {
            id: `${crew.id}-start-yard`,
            title: yardName,
            status: "YARD_START",
            startTime: crew.starting_point_start_time || "",
            endTime: crew.starting_point_end_time || "",
            address: crew.crew_yard_address || "",
            contract: "",
            property: {
              latitude: String(yardLat),
              longitude: String(yardLng),
            },
          };
          const first = crew.visits?.[0];
          const duplicate =
            first &&
            Number(first.property.latitude) ===
              Number(yardVisit.property.latitude) &&
            Number(first.property.longitude) ===
              Number(yardVisit.property.longitude);
          return {
            ...crew,
            visits: duplicate
              ? crew.visits
              : [yardVisit, ...(crew.visits || [])],
          };
        }
        return crew;
      });
      const newRoutesData: any = await Promise.all(
        currentRouteData.map(async (crew) => {
          console.log("crew", crew);
          if (!crew.visits || crew.visits.length < 1) {
            console.warn(
              `Crew ${crew.crew_name} has no visits, skipping route generation.`
            );
            return {
              id: crew.id,
              name: crew.crew_name,
              color: "#CCCCCC",
              visible: true,
              isActive: false,
              visits: [],
              polyline: undefined,
            };
          }

          if (crew.visits.length === 1) {
            const routeColor = stringToColor(crew.id);
            return {
              id: crew.id,
              name: crew?.crew_name,
              color: routeColor,
              polyline: undefined,
              visible: true,
              isActive: false,
              visits: crew.visits,
            };
          }

          const originVisit = crew.visits[0];
          const destinationVisit = crew.visits[crew.visits.length - 1];

          const waypoints = crew.visits.slice(1, -1).map((visits) => ({
            location: {
              lat: Number(visits.property.latitude),
              lng: Number(visits.property.longitude),
            },
            stopover: true,
          }));

          const routeColor = stringToColor(crew.id);
          try {
            const request: any = {
              origin: {
                lat: Number(originVisit.property.latitude),
                lng: Number(originVisit.property.longitude),
              },
              destination: {
                lat: Number(destinationVisit.property.latitude),
                lng: Number(destinationVisit.property.longitude),
              },
              waypoints: waypoints,
              travelMode: (window as any).google.maps.TravelMode.DRIVING, // Use window.google for mock
            };

            const response: any = await new Promise((resolve, reject) => {
              service.route(request, (result: any, status: any) => {
                if (status === "OK") {
                  resolve(result);
                } else {
                  reject(
                    `Directions request failed for ${crew?.crew_name} with status: ${status}`
                  );
                }
              });
            });

            if (response.routes.length > 0) {
              const path = response.routes[0].overview_path.map(
                (latLng: any) => ({
                  lat: latLng.lat(),
                  lng: latLng.lng(),
                })
              );

              return {
                id: crew.id,
                name: crew?.crew_name,
                color: routeColor,
                polyline: {
                  path: path,
                  strokeColor: routeColor,
                  strokeOpacity: 0.8,
                  strokeWeight: 4,
                  zIndex: 1,
                },
                visible: true,
                isActive: false,
                visits: crew.visits,
              };
            } else {
              console.warn(`No routes found for crew ${crew?.crew_name}`);
              return {
                ...crew,
                color: routeColor,
                polyline: undefined,
                isActive: false,
                visits: crew.visits,
              };
            }
          } catch (err: any) {
            console.error(
              `Error fetching route for crew ${crew?.crew_name}:`,
              err
            );
            return {
              ...crew,
              color: routeColor,
              polyline: undefined,
              isActive: false,
              visits: crew.visits,
            };
          }
        })
      );
      console.log("newRoutesData", newRoutesData);
      setRoutesData(newRoutesData);
      setLoadingRoutes(false);
    },
    []
  );

  console.log("routesData", routeData);

  // Callback for when map is loaded
  const onLoad = useCallback(
    (map: any) => {
      mapRef.current = map;
      directionsServiceRef.current = new (
        window as any
      ).google.maps.DirectionsService();
      if (directionsServiceRef.current && routeData.length > 0) {
        fetchAllRoutes(directionsServiceRef.current, routeData);
      }
    },
    [fetchAllRoutes, routeData]
  );

  // Callback for when map is unmounted
  const onUnmount = useCallback(() => {
    mapRef.current = null;
    directionsServiceRef.current = null;
    console.log("Map unmounted. Refs cleared.");
  }, []);

  // Effect to re-fetch routes when routeData prop changes
  useEffect(() => {
    if (isLoaded && directionsServiceRef.current && routeData.length > 0) {
      fetchAllRoutes(directionsServiceRef.current, routeData);
    } else if (isLoaded && routeData.length === 0) {
      setRoutesData([]);
      setLoadingRoutes(false);
    }
  }, [isLoaded, routeData, fetchAllRoutes]);

  // Function to toggle route visibility
  const toggleRouteVisibility = useCallback((id: string) => {
    setRoutesData((prevRoutes) =>
      prevRoutes.map((route) =>
        route.id === id ? { ...route, visible: !route.visible } : route
      )
    );
  }, []);

  // Function to highlight a route (make it active)
  const highlightRoute = useCallback((id: string) => {
    setRoutesData((prevRoutes) =>
      prevRoutes.map((route) => ({
        ...route,
        isActive: route.id === id ? !route.isActive : false,
        polyline: route.polyline
          ? {
              ...route.polyline,
              strokeWeight: route.id === id && !route.isActive ? 6 : 4,
              strokeOpacity: route.id === id && !route.isActive ? 1.0 : 0.8,
              zIndex: route.id === id && !route.isActive ? 2 : 1,
            }
          : undefined,
      }))
    );
  }, []);

  if (loadError)
    return <Alert message="Error loading Google Maps" type="error" showIcon />;
  if (!isLoaded)
    return (
      <Spin
        indicator={antIcon}
        tip="Loading Map..."
        size="large"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      />
    );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        padding: "0",
        maxWidth: "1200px",
        margin: "0 auto",
        height: "100%",
      }}
    >
      <Card className="routesCard">
        <div className="showAllRoutes">
          <div className="switchWrapper">
            <Switch
              defaultChecked
              onChange={(checked) => {
                setRoutesData((prevRoutes) =>
                  prevRoutes.map((r) => ({ ...r, visible: checked }))
                );
              }}
            />
            <span>Show all routes</span>
          </div>

          {/* <Button
            type="primary"
            onClick={() => {
              const allVisible = routesData.every((r) => r.visible);
              setRoutesData((prevRoutes) =>
                prevRoutes.map((r) => ({ ...r, visible: !allVisible }))
              );
            }}
          >
            Show all routes
          </Button> */}

          <Button
            onClick={() => setShowRoutes(false)}
            style={{
              marginLeft: "auto",
              height: "36px",
              width: "36px",
              border: "1px solid #DEE5E6",
              backgroundColor: "#fff",
            }}
          >
            <CloseOutlined />
          </Button>
        </div>
        <Space
          size={[8, 16]}
          wrap
          style={{ position: "absolute", top: "68px", left: "2px" }}
        >
          {routesData.map((route, i) => {
            const isVisible = route.visible ?? true;
            const isActive = route.isActive ?? false;
            console.log("route", route);
            return (
              <Tag
                key={route.id}
                color={isActive ? "blue" : isVisible ? route.color : "default"}
                onClick={() => highlightRoute(route.id)}
                // closable
                onClose={(e) => {
                  e.stopPropagation();
                  toggleRouteVisibility(route.id);
                }}
                className={`map_route_${i + 1}`}
                style={{
                  cursor: "pointer",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: isActive
                    ? "2px solid #1890ff"
                    : `1px solid ${isVisible ? route.color : "#d9d9d9"}`,
                  fontWeight: isActive ? "bold" : "normal",
                  fontSize: "14px",
                }}
              >
                {route.crew_name}
              </Tag>
            );
          })}
        </Space>

        {error && (
          <Alert
            message={error}
            type="warning"
            showIcon
            style={{ marginTop: "10px" }}
          />
        )}
      </Card>

      <div style={{ position: "relative", height: "100%" }}>
        {loadingRoutes && (
          <div className="mapLoader">
            <Spin indicator={antIcon} />
          </div>
        )}
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={mapCenter || { lat: 0, lng: 0 }}
          zoom={13}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            zoomControl: true,
            // styles: CUSTOM_MAP_STYLES, // Apply custom styles here
          }}
        >
          {routesData.map(
            (route) =>
              route.visible &&
              route.polyline &&
              route.polyline.path && (
                <Polyline
                  key={route.id}
                  path={route.polyline.path as any[]}
                  options={{
                    strokeColor: route.polyline.strokeColor,
                    strokeOpacity: route.polyline.strokeOpacity,
                    strokeWeight: route.polyline.strokeWeight,
                    zIndex: route.polyline.zIndex,
                  }}
                />
              )
          )}
          {routesData.map(
            (route) =>
              route.visible &&
              route.visits &&
              route.visits.map((visit, index) => (
                <Marker
                  key={`${route.id}-${visit.id}`}
                  position={{
                    lat: Number(visit.property.latitude),
                    lng: Number(visit.property.longitude),
                  }}
                  title={visit.title}
                  icon={{
                    path: (window as any).google.maps.SymbolPath.CIRCLE,
                    fillColor: route.color,
                    fillOpacity: 1,
                    strokeWeight: 1,
                    scale: 10,
                    labelOrigin: new (window as any).google.maps.Point(0, 0),
                  }}
                  label={{
                    text: String(index + 1),
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                />
              ))
          )}
        </GoogleMap>
      </div>
    </div>
  );
};

export default RoutesMap;
